import { useId } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { ProgressBar } from "@/components/design/ProgressBar";
import { ToplistCard, type ToplistItem } from "./ToplistCard";
import { ToplistSearch, type ToplistSearchSource } from "./ToplistSearch";

// Second step: one out of four, round after round, until there is a pick for
// every place. "None of these" brings the next four; the search on top finds
// whatever the rounds miss. Every pick shows up below and can be taken back.
// The pick that fills the last place moves on to the result by itself.
export function DiscoverStage({
  question,
  round,
  picks,
  keep,
  canSkip,
  search,
  onChoose,
  onSkip,
  onRemove,
  onChangeTags,
}: {
  // The game's own question, over every round.
  question: string;
  round: readonly ToplistItem[];
  // What is picked so far, in the order it was picked.
  picks: readonly ToplistItem[];
  // Places in the ranking, and so picks to make.
  keep: number;
  canSkip: boolean;
  search: ToplistSearchSource;
  onChoose: (id: string) => void;
  onSkip: () => void;
  onRemove: (id: string) => void;
  onChangeTags: () => void;
}) {
  const intl = useIntl();
  const questionId = useId();
  const picksId = useId();

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <h2 id={questionId} className="text-smycles-title font-medium">
          {question}
        </h2>
        <Button id="toplist-change-tags" variant="ghost" size="sm" onClick={onChangeTags}>
          <FormattedMessage id="grow.games.toplist.discover.changeTags" defaultMessage="Change categories" />
        </Button>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <ProgressBar
          className="flex-1"
          label={intl.formatMessage(
            { id: "grow.games.toplist.discover.progressLabel", defaultMessage: "Picks for your top {keep}" },
            { keep },
          )}
          done={picks.length}
          total={keep}
        />
        <p role="status" className="shrink-0 text-smycles-meta tabular-nums text-ink-muted">
          <FormattedMessage
            id="grow.games.toplist.discover.progress"
            defaultMessage="{count} of {keep}"
            values={{ count: picks.length, keep }}
          />
        </p>
      </div>

      <ToplistSearch
        className="mt-6"
        source={search}
        picked={new Set(picks.map((item) => item.id))}
        onChoose={onChoose}
      >
        {/* Keyed by position, not by id: the buttons stay mounted from round
            to round, so keyboard focus stays on the card that was chosen. */}
        <div
          role="group"
          aria-labelledby={questionId}
          aria-live="polite"
          className="mt-4 grid grid-cols-2 gap-3 @md:grid-cols-4"
        >
          {round.map((item, index) => (
            <ToplistCard key={index} id={`toplist-round-${index}`} item={item} onClick={() => onChoose(item.id)} />
          ))}
        </div>
        {canSkip ? (
          <Button variant="secondary" size="compact" className="mt-4" onClick={onSkip}>
            <FormattedMessage id="grow.games.toplist.discover.skip" defaultMessage="None of these" values={{ genus: search.genus }} />
          </Button>
        ) : null}
      </ToplistSearch>

      {picks.length > 0 ? (
        <div className="mt-8">
          <p id={picksId} className="smycles-field-label">
            <FormattedMessage id="grow.games.toplist.discover.picks" defaultMessage="Your picks" />
          </p>
          <ul aria-labelledby={picksId} className="mt-2 flex flex-wrap gap-2">
            {picks.map((item) => (
              <li key={item.id}>
                <Button
                  variant="chip"
                  size="sm"
                  onClick={() => onRemove(item.id)}
                  aria-label={intl.formatMessage(
                    { id: "grow.games.toplist.remove", defaultMessage: "Remove {title}" },
                    { title: item.title },
                  )}
                >
                  {/* The chip lays its content out as flex, where a plain space collapses. */}
                  {item.title}
                  <span aria-hidden="true" className="ml-1.5">
                    ×
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
